import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import type { User, Session } from '@supabase/supabase-js'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  initialized: boolean

  // Actions
  initialize: () => Promise<void>
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>
  signUpWithPassword: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  setSession: (session: Session | null) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      session: null,
      loading: false,
      initialized: false,

      initialize: async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          set({ session, user: session?.user ?? null, initialized: true })

          supabase.auth.onAuthStateChange((_event, session) => {
            set({ session, user: session?.user ?? null })
          })
        } catch {
          // Supabase not configured or network unavailable — still mark initialized
          // so ProtectedRoute redirects to /login instead of loading forever.
          set({ initialized: true })
        }
      },

      signInWithMagicLink: async (email: string) => {
        set({ loading: true })
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: window.location.origin + '/auth/callback'
          }
        })
        set({ loading: false })
        return { error: error?.message ?? null }
      },

      signInWithPassword: async (email: string, password: string) => {
        set({ loading: true })
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        set({ loading: false })
        return { error: error?.message ?? null }
      },

      signUpWithPassword: async (email: string, password: string) => {
        set({ loading: true })
        const { error } = await supabase.auth.signUp({ email, password })
        set({ loading: false })
        return { error: error?.message ?? null }
      },

      signOut: async () => {
        await supabase.auth.signOut()
        set({ user: null, session: null })
      },

      setSession: (session) => {
        set({ session, user: session?.user ?? null })
      }
    }),
    {
      name: 'vehicletco-auth',
      partialize: (state) => ({ session: state.session, user: state.user })
    }
  )
)
